import { withErrorCode } from '@/lib/errors'
import { formatUserFacingError } from '@/lib/errors'
import { useState, useRef } from 'react'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { parseCsv, type ParsedEmailEntry } from '@/lib/csv'
import type { BulkAddResult } from '../../services/admin.mailingList.service'

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  onBulkAdd: (entries: ParsedEmailEntry[]) => Promise<BulkAddResult>
}

const VALID_SEGMENTS = new Set(['general', 'film-updates', 'services', 'oddfest', 'shift'])

const LANGUAGE_MAP: Record<string, string> = {
  english: 'en',
  spanish: 'es',
  french: 'fr',
  portuguese: 'pt',
  german: 'de',
  italian: 'it',
  japanese: 'ja',
  chinese: 'zh',
  korean: 'ko',
  arabic: 'ar',
  russian: 'ru',
  hindi: 'hi',
}

function findHeader(headers: string[], candidates: string[]): string | undefined {
  const lower = headers.map((h) => h.toLowerCase().replace(/[\s_-]/g, ''))
  const idx = lower.findIndex((h) => candidates.some((c) => h === c.replace(/[\s_-]/g, '')))
  return idx !== -1 ? headers[idx] : undefined
}

function normalizeLanguage(raw: string): string | undefined {
  if (!raw) return undefined
  const trimmed = raw.trim()
  // Already an ISO 639-1 code (2-3 chars)
  if (/^[a-z]{2,3}$/i.test(trimmed)) return trimmed.toLowerCase()
  return LANGUAGE_MAP[trimmed.toLowerCase()] ?? trimmed.toLowerCase()
}

function normalizeSegments(raw: string): string[] | undefined {
  if (!raw) return undefined
  const parsed = raw
    .split(/[,;|]/)
    .map((s) => s.trim().toLowerCase())
    .filter((s) => VALID_SEGMENTS.has(s))
  return parsed.length > 0 ? parsed : undefined
}

function parseCsvToEntries(text: string): ParsedEmailEntry[] {
  const { headers, rows } = parseCsv(text)

  const emailHeader    = findHeader(headers, ['email', 'emailaddress', 'e-mail'])
  const firstHeader    = findHeader(headers, ['firstname', 'first', 'givenname'])
  const lastHeader     = findHeader(headers, ['lastname', 'last', 'surname', 'familyname'])
  const languageHeader = findHeader(headers, ['language', 'primarylanguage', 'lang'])
  const segmentsHeader = findHeader(headers, ['segments', 'segment', 'lists'])

  if (!emailHeader) return []

  return rows.map((row): ParsedEmailEntry => {
    const email      = (row[emailHeader] ?? '').trim().toLowerCase()
    const firstName  = firstHeader    ? (row[firstHeader] ?? '').trim()  : ''
    const lastName   = lastHeader     ? (row[lastHeader]  ?? '').trim()  : ''
    const language   = languageHeader ? normalizeLanguage(row[languageHeader] ?? '') : undefined
    const segments   = segmentsHeader ? normalizeSegments(row[segmentsHeader] ?? '') : undefined
    const isValid    = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
    return { email, firstName, lastName, language, segments, isValid }
  })
}

export function CsvImportDialog({ open, onOpenChange, onBulkAdd }: Props) {
  const [entries, setEntries] = useState<ParsedEmailEntry[] | null>(null)
  const [filename, setFilename] = useState('')
  const [saving, setSaving] = useState(false)
  const [result, setResult] = useState<BulkAddResult | null>(null)
  const [error, setError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    setFilename(file.name)
    setError(null)
    setEntries(null)

    const reader = new FileReader()
    reader.onload = (evt) => {
      const text = evt.target?.result as string
      try {
        const parsed = parseCsvToEntries(text)
        if (parsed.length === 0) {
          setError(withErrorCode('No email column found. Ensure a column named "email" exists.', 'app/validation'))
          return
        }
        setEntries(parsed)
      } catch {
        setError(withErrorCode('Failed to parse the CSV file.', 'app/validation'))
      }
    }
    reader.readAsText(file)
  }

  async function handleImport() {
    if (!entries) return
    const valid = entries.filter((e) => e.isValid)
    if (valid.length === 0) return
    setSaving(true)
    setError(null)
    try {
      const res = await onBulkAdd(entries)
      setResult(res)
    } catch (err) {
      setError(formatUserFacingError(err, 'Import failed.'))
    } finally {
      setSaving(false)
    }
  }

  function handleClose() {
    setEntries(null)
    setFilename('')
    setResult(null)
    setError(null)
    if (fileInputRef.current) fileInputRef.current.value = ''
    onOpenChange(false)
  }

  const validCount = entries?.filter((e) => e.isValid).length ?? 0
  const invalidCount = entries?.filter((e) => !e.isValid).length ?? 0
  const preview = entries?.slice(0, 10) ?? []

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) { handleClose() } else { onOpenChange(true) } }}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Import CSV</DialogTitle>
          <DialogDescription>
            Upload a CSV file with an <code className="text-xs">email</code> column.
            Optionally include <code className="text-xs">firstName</code>,{' '}
            <code className="text-xs">lastName</code>,{' '}
            <code className="text-xs">language</code> (name or ISO code), and{' '}
            <code className="text-xs">segments</code> (comma-separated:{' '}
            <code className="text-xs">general</code>,{' '}
            <code className="text-xs">film-updates</code>,{' '}
            <code className="text-xs">services</code>,{' '}
            <code className="text-xs">oddfest</code>,{' '}
            <code className="text-xs">shift</code>).
          </DialogDescription>
        </DialogHeader>

        {result ? (
          <div className="space-y-3 py-2">
            <div className="flex gap-4 text-sm">
              <span className="text-green-600 dark:text-green-400">
                Added: <strong>{result.added}</strong>
              </span>
              <span className="text-muted-foreground">
                Skipped (already exists): <strong>{result.skipped}</strong>
              </span>
            </div>
            {result.errors.length > 0 && (
              <div className="space-y-1">
                <p className="text-xs font-medium text-destructive">Errors:</p>
                <ul className="space-y-0.5 text-xs text-destructive">
                  {result.errors.map((e, i) => (
                    <li key={i}>{withErrorCode(e, 'mailing-list/import')}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            <div className="space-y-1">
              <Label htmlFor="csv-upload">CSV File</Label>
              <input
                id="csv-upload"
                ref={fileInputRef}
                type="file"
                accept=".csv,text/csv"
                onChange={handleFile}
                className="block w-full text-sm text-muted-foreground file:mr-4 file:rounded-md file:border-0 file:bg-primary file:px-3 file:py-1.5 file:text-xs file:font-medium file:text-primary-foreground hover:file:bg-primary/90"
              />
              {filename && (
                <p className="text-xs text-muted-foreground">Selected: {filename}</p>
              )}
            </div>

            {entries && entries.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  <Badge variant="default">{validCount} valid</Badge>
                  {invalidCount > 0 && (
                    <Badge variant="destructive">{invalidCount} invalid</Badge>
                  )}
                  {entries.length > 10 && (
                    <span className="text-xs text-muted-foreground">
                      Showing first 10 of {entries.length}
                    </span>
                  )}
                </div>
                <div className="overflow-x-auto rounded-md border">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b bg-muted/50 text-muted-foreground">
                        <th className="px-3 py-2 text-left font-medium">Email</th>
                        <th className="px-3 py-2 text-left font-medium">First Name</th>
                        <th className="px-3 py-2 text-left font-medium">Last Name</th>
                        {preview.some((e) => e.language != null) && (
                          <th className="px-3 py-2 text-left font-medium">Language</th>
                        )}
                        {preview.some((e) => e.segments != null) && (
                          <th className="px-3 py-2 text-left font-medium">Segments</th>
                        )}
                        <th className="px-3 py-2 text-left font-medium">Valid</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.map((entry, i) => (
                        <tr
                          key={i}
                          className={!entry.isValid ? 'bg-destructive/10' : ''}
                        >
                          <td className={`px-3 py-1.5 font-mono ${!entry.isValid ? 'text-destructive' : ''}`}>
                            {entry.email || <em className="text-muted-foreground">empty</em>}
                          </td>
                          <td className="px-3 py-1.5">{entry.firstName}</td>
                          <td className="px-3 py-1.5">{entry.lastName}</td>
                          {preview.some((e) => e.language != null) && (
                            <td className="px-3 py-1.5 text-muted-foreground">{entry.language ?? '—'}</td>
                          )}
                          {preview.some((e) => e.segments != null) && (
                            <td className="px-3 py-1.5 text-muted-foreground">
                              {entry.segments?.join(', ') ?? '—'}
                            </td>
                          )}
                          <td className="px-3 py-1.5">
                            {entry.isValid ? (
                              <span className="text-green-600 dark:text-green-400">✓</span>
                            ) : (
                              <span className="text-destructive">✗</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {error && <p className="text-sm text-destructive">{error}</p>}
          </div>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={handleClose}>
            {result ? 'Close' : 'Cancel'}
          </Button>
          {!result && (
            <Button
              type="button"
              onClick={() => void handleImport()}
              disabled={saving || validCount === 0}
            >
              {saving ? 'Importing…' : `Import ${validCount} Members`}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
